/**
 * Entitlements — what THIS deployment is licensed to do, as distinct from what a person may do
 * (F-0188, `docs/concept-agent-token-usage.md` §3.2).
 *
 * ⛔ **Not a permission.** A permission answers *who* may act and is granted to roles; an entitlement
 * answers *whether the capability exists here at all* and is granted to the deployment. A System Admin
 * holding every permission is still refused an act the deployment is not entitled to. Keeping the two
 * apart is what lets a paid plan switch a capability off without editing a single role.
 *
 * A closed vocabulary for the reason `PERMISSIONS` is one: a free-form string checked at a call site
 * is a switch nobody can list.
 */

export const ENTITLEMENTS = [
  'agent_usage.custom_interval',
  'expiring_access',
  'agent_approvers',
] as const;
export type EntitlementType = (typeof ENTITLEMENTS)[number];

export const ENTITLEMENT_DESCRIPTIONS: Record<EntitlementType, string> = {
  'agent_usage.custom_interval':
    'Choose how often bridges report token usage. Without it the interval is fixed at 10 minutes.',
  expiring_access:
    'Give an agent access for a chosen time (F-0713, D-0413). Without it access given through a request or the picker has no end.',
  agent_approvers:
    'Let authorized agents answer requests for agent access (F-0713 slice 8, ADR-187; Enterprise, D-0229 and D-0295). Without it only people answer and the setting is not offered.',
};

/** The structured code a refusal carries, so a client can render the plan state rather than an error. */
export const ENTITLEMENT_REQUIRED_CODE = 'ENTITLEMENT_REQUIRED';

export function isEntitlement(value: unknown): value is EntitlementType {
  return typeof value === 'string' && (ENTITLEMENTS as readonly string[]).includes(value);
}

/**
 * The entitlements a comma list withholds. Unknown names are returned separately so the server can
 * refuse to boot on a typo — a misspelt withholding that silently withheld nothing would leave a paid
 * capability on.
 */
export function parseWithheldEntitlements(value: string | undefined | null): {
  withheld: EntitlementType[];
  unknown: string[];
} {
  const names = (value ?? '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  return {
    withheld: [...new Set(names.filter(isEntitlement))],
    unknown: names.filter((name) => !isEntitlement(name)),
  };
}
